import { useEffect, useMemo, useState } from 'react';
import { parseAdMeta, phoneHref, type AdSize } from '../../lib/adMeta';
import { isSupabaseConfigured, supabase } from '../../lib/supabase';

type CmsPost = {
  id: string;
  title: string;
  body: string | null;
  image_url: string | null;
  link_url: string | null;
  created_at: string;
};

function isVideoUrl(url: string) {
  const cleanUrl = url.split('?')[0].toLowerCase();
  return ['.mp4', '.webm', '.mov', '.m4v', '.ogg'].some((ext) => cleanUrl.endsWith(ext));
}

const sizeClasses: Record<AdSize, string> = {
  small: 'aspect-[4/1]',
  medium: 'aspect-[3/1]',
  large: 'aspect-[2/1]',
};

const maxWidthClasses: Record<AdSize, string> = {
  small: 'max-w-2xl',
  medium: 'max-w-3xl',
  large: 'max-w-4xl',
};

export default function PublicPosts() {
  const [posts, setPosts] = useState<CmsPost[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const client = supabase;
    if (!isSupabaseConfigured || !client) return;

    let active = true;
    const load = async () => {
      const { data } = await client
        .from('cms_posts')
        .select('id,title,body,image_url,link_url,created_at')
        .eq('is_published', true)
        .order('sort_order', { ascending: false })
        .order('created_at', { ascending: false });

      if (active && data) {
        setPosts(data);
        setCurrentIndex((index) => (data.length ? Math.min(index, data.length - 1) : 0));
      }
    };

    void load();
    return () => {
      active = false;
    };
  }, []);

  const post = posts[currentIndex];
  const meta = useMemo(() => parseAdMeta(post?.body), [post?.body]);

  useEffect(() => {
    if (posts.length <= 1 || !post) return;
    const timer = window.setTimeout(() => {
      setCurrentIndex((index) => (index + 1) % posts.length);
    }, meta.displaySeconds * 1000);
    return () => window.clearTimeout(timer);
  }, [meta.displaySeconds, post?.id, posts.length]);

  if (!post) return null;

  const mediaUrl = post.image_url;
  const isVideo = mediaUrl ? isVideoUrl(mediaUrl) : false;
  const mediaClass = sizeClasses[meta.size];
  const phoneLink = phoneHref(meta.phoneNumber);

  const media = mediaUrl ? (
    isVideo ? (
      <video
        key={post.id}
        src={mediaUrl}
        className={`block w-full ${mediaClass} object-cover bg-black`}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
      />
    ) : (
      <img
        key={post.id}
        src={mediaUrl}
        alt="إعلان"
        className={`block w-full ${mediaClass} object-cover`}
      />
    )
  ) : (
    <div className={`w-full ${mediaClass} grid place-items-center px-5 text-center bg-black/20`}>
      <p className="text-white font-bold text-lg">{post.title}</p>
    </div>
  );

  return (
    <section className="relative z-10 w-full px-2 sm:px-4 mt-1 mb-7" dir="rtl">
      <div className={`w-full ${maxWidthClasses[meta.size]} mx-auto`}>
        <div className="flex items-center gap-3 mb-3">
          <div className="h-px flex-1 bg-gradient-to-l from-[#F5A623]/60 to-transparent" />
          <h2 className="text-sm sm:text-base font-bold text-[#F5A623]">إعلان</h2>
          <div className="h-px flex-1 bg-gradient-to-r from-[#F5A623]/60 to-transparent" />
        </div>

        <article
          key={post.id}
          className="relative overflow-hidden rounded-2xl sm:rounded-3xl transition-all duration-300"
          style={{
            background: 'linear-gradient(135deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03))',
            border: '1px solid rgba(245,166,35,0.45)',
            boxShadow: '0 10px 35px rgba(0,0,0,0.35), 0 0 28px rgba(245,166,35,0.08)',
          }}
        >
          {post.link_url ? (
            <a href={post.link_url} target="_blank" rel="noreferrer" className="block" aria-label="فتح رابط الإعلان">
              {media}
            </a>
          ) : (
            media
          )}

          {(meta.displayText || phoneLink || post.link_url) && (
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between px-4 py-3 bg-black/35 border-t border-white/10">
              {meta.displayText ? (
                <p className="text-sm sm:text-base text-white leading-6 flex-1 whitespace-pre-line">
                  {meta.displayText}
                </p>
              ) : (
                <span className="flex-1" />
              )}

              <div className="flex flex-wrap gap-2 shrink-0">
                {post.link_url && (
                  <a
                    href={post.link_url}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-xl bg-[#F5A623] text-black font-bold text-xs sm:text-sm px-4 py-2"
                  >
                    فتح الرابط
                  </a>
                )}
                {phoneLink && (
                  <a
                    href={phoneLink}
                    className="rounded-xl border border-[#F5A623]/60 text-[#F5A623] font-bold text-xs sm:text-sm px-4 py-2"
                  >
                    اتصال {meta.phoneNumber}
                  </a>
                )}
              </div>
            </div>
          )}
        </article>

        {posts.length > 1 && (
          <div className="flex justify-center gap-1.5 mt-3" aria-label="مؤشر الإعلانات">
            {posts.map((item, index) => (
              <button
                key={item.id}
                type="button"
                aria-label={`الإعلان ${index + 1}`}
                onClick={() => setCurrentIndex(index)}
                className={`h-1.5 rounded-full transition-all ${index === currentIndex ? 'w-7 bg-[#F5A623]' : 'w-2 bg-white/25'}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
